import React, { useState, useEffect, useCallback } from 'react';
import './GanttZoomHUD.css';

// Auto-zoom presets
const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'this-week', label: 'Week' },
  { id: 'this-month', label: 'Month' },
  { id: 'this-quarter', label: 'Qtr' },
  { id: 'this-year', label: 'Year' },
  { id: 'all', label: 'All' },
];

// Icons
const ZoomInIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const ZoomOutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const TodayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <circle cx="12" cy="15" r="2" fill="currentColor" />
  </svg>
);

/**
 * GanttZoomHUD Component
 * 
 * Floating HUD overlay in the top-right of the Gantt canvas with:
 * - Zoom in/out buttons
 * - Go to Today button
 * - Time range presets (Today, Week, Month, Quarter, Year, All)
 */
const GanttZoomHUD = () => {
  const [activePreset, setActivePreset] = useState('all');

  // Listen for preset changes from GanttView
  useEffect(() => {
    const handlePresetChange = (e) => {
      setActivePreset(e.detail.preset);
    };
    
    window.addEventListener('ganttPresetChanged', handlePresetChange);
    
    return () => {
      window.removeEventListener('ganttPresetChanged', handlePresetChange);
    };
  }, []);

  // Dispatch zoom events to GanttView
  const handleZoomIn = useCallback(() => {
    window.dispatchEvent(new CustomEvent('ganttZoom', { detail: { action: 'in' } }));
  }, []);

  const handleZoomOut = useCallback(() => {
    window.dispatchEvent(new CustomEvent('ganttZoom', { detail: { action: 'out' } }));
  }, []);

  // Go to today
  const handleGoToToday = useCallback(() => {
    window.dispatchEvent(new CustomEvent('ganttGoToToday'));
  }, []);
  
  // Dispatch preset change events to GanttView
  const handlePresetChange = useCallback((presetId) => {
    setActivePreset(presetId);
    window.dispatchEvent(new CustomEvent('ganttPreset', { 
      detail: { preset: presetId } 
    }));
  }, []);

  return (
    <div className="gantt-zoom-hud">
      {/* Zoom controls */}
      <div className="gantt-zoom-hud__group gantt-zoom-hud__group--zoom">
        <button
          className="gantt-zoom-hud__btn"
          onClick={handleZoomOut}
          title="Zoom out (show more time)"
        >
          <ZoomOutIcon />
        </button>
        <button
          className="gantt-zoom-hud__btn"
          onClick={handleZoomIn}
          title="Zoom in (show less time)"
        >
          <ZoomInIcon />
        </button>
      </div>

      <div className="gantt-zoom-hud__divider" />

      {/* Go to today */}
      <button
        className="gantt-zoom-hud__btn"
        onClick={handleGoToToday}
        title="Go to today"
      >
        <TodayIcon />
      </button>

      <div className="gantt-zoom-hud__divider" />

      {/* Time range presets */}
      <div className="gantt-zoom-hud__group gantt-zoom-hud__group--presets">
        <span className="gantt-zoom-hud__label">Fit:</span>
        {RANGE_PRESETS.map(preset => (
          <button
            key={preset.id}
            className={`gantt-zoom-hud__preset ${activePreset === preset.id ? 'gantt-zoom-hud__preset--active' : ''}`}
            onClick={() => handlePresetChange(preset.id)}
            title={`Zoom to fit ${preset.label}`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default GanttZoomHUD;
